import { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { INDIA_STATES } from '../constants/indiaStates';
import GstinLookup from '../components/GstinLookup';
import ImportModal from '../components/ImportModal';
import api, { BASE_URL } from '../api';
import './Dashboard.css';

const COMPANY_TYPE_LABELS = {
  pvt_ltd: 'Private Limited',
  llp: 'LLP',
  proprietorship: 'Proprietorship',
  partnership: 'Partnership',
  public_ltd: 'Public Limited',
  section8: 'Section 8',
};

const BASE_TABS = ['Company Profile', 'Manage Companies', 'Compliance Applicability', 'TDS Sections', 'Import History', 'Alert Preferences'];

// What a customer_owner (self-signup tenant owner) sees. TDS Sections is
// absent by design, not oversight: that table has no company_id, so it is
// one shared statutory list (194C, 194J...) that NEEDCFO maintains centrally -
// a customer editing it would change it for every other customer. They still
// read it everywhere it matters (the TDS entry dropdowns).
const CUSTOMER_OWNER_TABS = [
  'Company Profile', 'Manage Companies', 'Compliance Applicability',
  'TDS Sections', 'Import History', 'Alert Preferences', 'Tally Ledger Mapping',
  'API Keys', 'Tally Connector', 'Users', 'Plan & Billing',
];

// Public releases-only repo: it carries the built .exe and nothing else, so
// the connector's source stays private while clients get a plain download link
// with no GitHub login. /releases/latest/download/ always resolves to the
// newest published asset, so this URL never needs updating per version.
const CONNECTOR_DOWNLOAD_URL =
  'https://github.com/NEEDCFO2017/tally-connector-releases/releases/latest/download/TallyConnector.exe';

// Contact shown on the upgrade screen and the Help tab.
const SUPPORT = {
  company: 'ASA NEEDCFO Finvisors Pvt. Ltd.',
  phone: '9350880086',
  email: 'contactneedcfo@gmail.com',
};

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const ROLE_LABELS = {
  owner_admin: 'Owner/Admin',
  customer_owner: 'Admin',
  consultant_staff: 'Consultant/Staff',
  client: 'Client',
};

const EMPTY_USER_FORM = { name: '', email: '', password: '', role: 'consultant_staff' };

const IMPORT_MODULE_LABELS = {
  client: 'Client Master',
  dsc: 'DSC Register',
  itr: 'ITR Filing Status',
  audit: 'Audit Status',
  gst: 'GST Returns',
  gst_data: 'GST Data Entry',
  tds_data: 'TDS Data Entry',
  tds_deposit: 'TDS Deposit',
  tds_return_filing: 'TDS Return Filing',
  financials: 'Financials',
  roc: 'ROC/MCA',
  tally_ledger_mapping: 'Tally Ledger Mapping',
};

const CATEGORY_LABELS = {
  GST: 'GST Returns',
  TDS: 'TDS / TCS',
  ROC: 'ROC / MCA',
  PF: 'PF',
  ESIC: 'ESIC',
  Income_Tax: 'Income Tax',
};

const EMPTY_TDS_SECTION = {
  old_section: '',
  new_section: '',
  description: '',
  rate_percent: '',
  threshold_amount: '',
  effective_from: '',
  is_active: true,
  remarks: '',
};

const GST_FIELD_LABELS = {
  output_igst: 'Output IGST',
  output_cgst: 'Output CGST',
  output_sgst: 'Output SGST',
  input_igst: 'Input IGST',
  input_cgst: 'Input CGST',
  input_sgst: 'Input SGST',
  sales: 'Sales',
};

const FINANCIALS_FIELD_LABELS = {
  taxable_sales: 'Taxable Sales',
  export_exempt_sales: 'Export / Exempt Sales',
  direct_expenses: 'Direct Expenses',
  indirect_expenses: 'Indirect Expenses',
  other_income: 'Other Income',
  cash_balance: 'Cash Balance',
  receivables: 'Receivables',
};

const MAPPING_CATEGORY_LABELS = { gst: 'GST', tds: 'TDS', financials: 'Financials' };

const EMPTY_LEDGER_MAPPING = { category: 'gst', mapped_field: 'output_cgst', tally_ledger_name: '', rate: '' };

const EMPTY_API_KEY_FORM = { label: '' };

function Settings() {
  const {
    selectedCompany,
    selectedCompanyId,
    companies,
    refreshCompanies,
    selectCompany,
    deleteCompany,
    openAddModal,
    openEditModal,
  } = useCompany();
  const { user: currentUser } = useAuth();
  const isOwnerAdmin = currentUser?.role === 'owner_admin';
  const isCustomerOwner = currentUser?.role === 'customer_owner';
  const canManageUsers = isOwnerAdmin || isCustomerOwner;
  const TABS = isOwnerAdmin
    ? [...BASE_TABS, 'Tally Ledger Mapping', 'API Keys', 'Tally Connector', 'Users', 'Plan & Billing', 'Subscribers']
    : isCustomerOwner
    ? CUSTOMER_OWNER_TABS
    : BASE_TABS;

  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [form, setForm] = useState({
    name: '', cin: '', gstin: '', pan: '', state: '', itWard: '', auditType: 'Non-Audit',
    email: '', mobile: '', client_mode: 'Professional',
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [tdsSections, setTdsSections] = useState([]);
  const [tdsModalOpen, setTdsModalOpen] = useState(false);
  const [editingTdsId, setEditingTdsId] = useState(null);
  const [tdsForm, setTdsForm] = useState(EMPTY_TDS_SECTION);
  const [tdsSaving, setTdsSaving] = useState(false);

  const [categorySettings, setCategorySettings] = useState([]);
  const [togglingCategory, setTogglingCategory] = useState(null);

  const [plan, setPlan] = useState(null);
  const [planSaving, setPlanSaving] = useState(false);
  const [billingMonths, setBillingMonths] = useState(1);

  const [regenTarget, setRegenTarget] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);

  const [subscribers, setSubscribers] = useState([]);
  const [subFilter, setSubFilter] = useState('all');
  const [activateTarget, setActivateTarget] = useState(null); // subscriber row
  const [activateForm, setActivateForm] = useState({ months: 1, payment_reference: '', notes: '' });
  const [activating, setActivating] = useState(false);
  const [subDeleteTarget, setSubDeleteTarget] = useState(null); // subscriber row
  const [subDeletePreview, setSubDeletePreview] = useState(null); // null while loading
  const [subDeleteConfirmText, setSubDeleteConfirmText] = useState('');
  const [subDeleting, setSubDeleting] = useState(false);

  const [alertPrefs, setAlertPrefs] = useState(null);
  const [alertPrefsForm, setAlertPrefsForm] = useState(null);
  const [savingAlertPrefs, setSavingAlertPrefs] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState(null); // { ok, message }
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  const [formApplicability, setFormApplicability] = useState([]);
  const [savingForm, setSavingForm] = useState(null);
  const [formRemarksDraft, setFormRemarksDraft] = useState({});
  const [gstWarningPending, setGstWarningPending] = useState(null); // { proceed }

  const [periodModalForm, setPeriodModalForm] = useState(null); // the form row, or null when closed
  const [periodRules, setPeriodRules] = useState([]);
  const [periodRuleForm, setPeriodRuleForm] = useState({ fy_year: '', period: '', applicability: 'Yes', remarks: '' });
  const [periodSaving, setPeriodSaving] = useState(false);

  const [importHistory, setImportHistory] = useState([]);
  const [deletePreview, setDeletePreview] = useState(null); // { history, ...previewData }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(false);

  const [users, setUsers] = useState([]);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [userForm, setUserForm] = useState(EMPTY_USER_FORM);
  const [userSaving, setUserSaving] = useState(false);
  const [userDeleteTarget, setUserDeleteTarget] = useState(null);

  const [assignmentsUser, setAssignmentsUser] = useState(null);
  const [userAssignments, setUserAssignments] = useState([]);
  const [assignmentForm, setAssignmentForm] = useState({ company_id: '', client_id: '' });
  const [assignmentClients, setAssignmentClients] = useState([]);
  const [assignmentClientNames, setAssignmentClientNames] = useState({});
  const [assignmentSaving, setAssignmentSaving] = useState(false);

  const [ledgerMappings, setLedgerMappings] = useState([]);
  const [ledgerMappingModalOpen, setLedgerMappingModalOpen] = useState(false);
  const [editingLedgerMappingId, setEditingLedgerMappingId] = useState(null);
  const [ledgerMappingForm, setLedgerMappingForm] = useState(EMPTY_LEDGER_MAPPING);
  const [ledgerMappingSaving, setLedgerMappingSaving] = useState(false);
  const [ledgerMappingDeleteTarget, setLedgerMappingDeleteTarget] = useState(null);
  const [ledgerMappingImportOpen, setLedgerMappingImportOpen] = useState(false);

  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [apiKeyForm, setApiKeyForm] = useState(EMPTY_API_KEY_FORM);
  const [apiKeySaving, setApiKeySaving] = useState(false);
  const [newApiKeyPlaintext, setNewApiKeyPlaintext] = useState(null); // { label, plaintext_key, ... } shown once
  const [apiKeyRevokeTarget, setApiKeyRevokeTarget] = useState(null);

  const refreshImportHistory = () => {
    if (!selectedCompanyId) return;
    api.get('/import/history', { params: { company_id: selectedCompanyId } }).then((res) => setImportHistory(res.data));
  };

  useEffect(() => {
    refreshImportHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const openDeletePreview = (history) => {
    setPreviewLoading(true);
    api
      .get(`/import/history/${history.id}/preview-delete`)
      .then((res) => setDeletePreview({ history, ...res.data }))
      .finally(() => setPreviewLoading(false));
  };

  const confirmDeleteBatch = () => {
    if (!deletePreview) return;
    setDeletingBatch(true);
    api
      .post(`/import/history/${deletePreview.history.id}/delete`)
      .then((res) => {
        refreshImportHistory();
        setToast(
          `${res.data.rows_deleted} record(s) removed` +
            (res.data.rows_skipped_modified > 0 ? `, ${res.data.rows_skipped_modified} skipped (edited since import)` : '')
        );
        setTimeout(() => setToast(null), 4000);
        setDeletePreview(null);
      })
      .finally(() => setDeletingBatch(false));
  };

  const refreshCategorySettings = () => {
    if (!selectedCompanyId) return;
    api.get(`/companies/${selectedCompanyId}/category-settings`).then((res) => setCategorySettings(res.data));
  };

  useEffect(() => {
    refreshCategorySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const refreshSubscribers = () => {
    if (!isOwnerAdmin) return;
    api.get('/admin/subscribers').then((res) => setSubscribers(res.data)).catch(() => {});
  };

  const openSubscriberDelete = (s) => {
    setSubDeleteTarget(s);
    setSubDeletePreview(null);
    setSubDeleteConfirmText('');
    // The dialog stays disabled until this lands, so nobody can confirm a
    // deletion before seeing what it actually removes.
    api
      .get(`/admin/subscribers/${s.company_id}/delete-preview`)
      .then((res) => setSubDeletePreview(res.data))
      .catch((err) => {
        setToast(err.response?.data?.detail || 'Could not load what would be deleted');
        setSubDeleteTarget(null);
      });
  };

  const closeSubscriberDelete = () => {
    if (subDeleting) return;
    setSubDeleteTarget(null);
    setSubDeletePreview(null);
    setSubDeleteConfirmText('');
  };

  const confirmSubscriberDelete = () => {
    if (!subDeleteTarget || subDeleting) return;
    setSubDeleting(true);
    api
      .delete(`/admin/subscribers/${subDeleteTarget.company_id}`)
      .then((res) => {
        const d = res.data;
        const bits = [`${d.company_name} deleted`];
        if (d.users_deleted.length) bits.push(`${d.users_deleted.length} user account(s) removed`);
        if (d.records_deleted) bits.push(`${d.records_deleted} record(s) removed`);
        setToast(bits.join(' · '));
        setSubDeleteTarget(null);
        setSubDeletePreview(null);
        setSubDeleteConfirmText('');
        refreshSubscribers();
        // The deleted company may have been in the switcher, so the shared
        // company list has to be reloaded too, not just this table.
        refreshCompanies();
      })
      .catch((err) => setToast(err.response?.data?.detail || 'Delete failed'))
      .finally(() => setSubDeleting(false));
  };

  useEffect(() => {
    refreshSubscribers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnerAdmin]);

  const submitActivation = () => {
    if (!activateTarget) return;
    setActivating(true);
    api
      .put(`/companies/${activateTarget.company_id}/plan`, {
        plan: 'pro',
        months: activateForm.months ? Number(activateForm.months) : null,
        payment_reference: activateForm.payment_reference || null,
        notes: activateForm.notes || null,
      })
      .then(() => {
        setToast(`${activateTarget.company_name} activated on Pro ✓`);
        setTimeout(() => setToast(null), 4000);
        setActivateTarget(null);
        refreshSubscribers();
        refreshPlan();
      })
      .catch((err) => {
        setToast(err.response?.data?.detail || 'Could not activate');
        setTimeout(() => setToast(null), 4000);
      })
      .finally(() => setActivating(false));
  };

  const refreshPlan = () => {
    if (!selectedCompanyId) return;
    api.get(`/companies/${selectedCompanyId}/plan`).then((res) => setPlan(res.data));
  };

  useEffect(() => {
    refreshPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const setCompanyPlan = (newPlan, months) => {
    if (!selectedCompanyId) return;
    setPlanSaving(true);
    api
      .put(`/companies/${selectedCompanyId}/plan`, { plan: newPlan, months: months || null })
      .then((res) => {
        setPlan(res.data);
        setToast(newPlan === 'pro' ? 'Upgraded to Pro ✓' : 'Moved to Free plan');
        setTimeout(() => setToast(null), 3000);
      })
      .catch((err) => {
        setToast(err.response?.data?.detail || 'Could not change plan');
        setTimeout(() => setToast(null), 4000);
      })
      .finally(() => setPlanSaving(false));
  };

  const refreshAlertPreferences = () => {
    if (!selectedCompanyId) return;
    api.get(`/companies/${selectedCompanyId}/alert-preferences`).then((res) => {
      setAlertPrefs(res.data);
      setAlertPrefsForm({
        notification_email: res.data.notification_email || '',
        additional_emails: res.data.additional_emails || '',
        notification_mobile: res.data.notification_mobile || '',
        additional_mobiles: res.data.additional_mobiles || '',
        due_date_reminder_enabled: res.data.due_date_reminder_enabled,
        reminder_days_before: res.data.reminder_days_before,
        penalty_digest_enabled: res.data.penalty_digest_enabled,
      });
      setTestEmailStatus(null);
    });
  };

  useEffect(() => {
    refreshAlertPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const saveAlertPreferences = () => {
    if (!selectedCompanyId || !alertPrefsForm) return;
    setSavingAlertPrefs(true);
    api
      .put(`/companies/${selectedCompanyId}/alert-preferences`, {
        ...alertPrefsForm,
        notification_email: alertPrefsForm.notification_email || null,
        reminder_days_before: Number(alertPrefsForm.reminder_days_before) || 7,
      })
      .then(() => {
        refreshAlertPreferences();
        setToast('Alert preferences saved ✓');
        setTimeout(() => setToast(null), 3000);
      })
      .finally(() => setSavingAlertPrefs(false));
  };

  const sendTestAlertEmail = () => {
    if (!selectedCompanyId) return;
    setSendingTestEmail(true);
    setTestEmailStatus(null);
    api
      .post(`/companies/${selectedCompanyId}/alert-preferences/test-email`)
      .then((res) => setTestEmailStatus({ ok: true, message: `Test email sent to ${res.data.to}` }))
      .catch((err) => setTestEmailStatus({ ok: false, message: err.response?.data?.detail || err.message }))
      .finally(() => setSendingTestEmail(false));
  };

  const toggleCategoryApplicability = (row) => {
    setTogglingCategory(row.category);
    api
      .put(`/companies/${selectedCompanyId}/category-settings/${row.category}`, {
        is_applicable: !row.is_applicable,
      })
      .then(() => {
        refreshCategorySettings();
        setToast(
          row.is_applicable
            ? `${CATEGORY_LABELS[row.category] || row.category} marked Not Applicable`
            : `${CATEGORY_LABELS[row.category] || row.category} marked Applicable`
        );
        setTimeout(() => setToast(null), 3000);
      })
      .finally(() => setTogglingCategory(null));
  };

  const refreshFormApplicability = () => {
    if (!selectedCompanyId) return;
    api.get(`/companies/${selectedCompanyId}/form-applicability`).then((res) => {
      setFormApplicability(res.data);
      const drafts = {};
      res.data.forEach((row) => {
        drafts[row.form_name] = row.remarks || '';
      });
      setFormRemarksDraft(drafts);
    });
  };

  useEffect(() => {
    refreshFormApplicability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const GST_MANDATORY_NIL_FORMS = ['GSTR-1', 'GSTR-3B'];

  const doUpdateFormApplicability = (row, applicability) => {
    setSavingForm(row.form_name);
    api
      .put(`/companies/${selectedCompanyId}/form-applicability/${encodeURIComponent(row.form_name)}`, {
        applicability,
        remarks: formRemarksDraft[row.form_name] || null,
      })
      .then(() => {
        refreshFormApplicability();
        setToast(`${row.form_name} marked ${applicability === 'Yes' ? 'Applicable' : applicability}`);
        setTimeout(() => setToast(null), 3000);
      })
      .finally(() => setSavingForm(null));
  };

  const updateFormApplicability = (row, applicability) => {
    if (GST_MANDATORY_NIL_FORMS.includes(row.form_name) && applicability !== 'Yes') {
      setGstWarningPending({ proceed: () => doUpdateFormApplicability(row, applicability) });
      return;
    }
    doUpdateFormApplicability(row, applicability);
  };

  const saveFormRemarks = (row) => {
    setSavingForm(row.form_name);
    api
      .put(`/companies/${selectedCompanyId}/form-applicability/${encodeURIComponent(row.form_name)}`, {
        applicability: row.applicability,
        remarks: formRemarksDraft[row.form_name] || null,
      })
      .then(() => {
        refreshFormApplicability();
        setToast('Remarks saved ✓');
        setTimeout(() => setToast(null), 3000);
      })
      .finally(() => setSavingForm(null));
  };

  const MONTH_LABELS = {
    '1': 'January', '2': 'February', '3': 'March', '4': 'April', '5': 'May', '6': 'June',
    '7': 'July', '8': 'August', '9': 'September', '10': 'October', '11': 'November', '12': 'December',
  };
  // FY order (Apr first, Mar last) for the period dropdown, matching how the backend orders them.
  const MONTH_PERIOD_OPTIONS = ['4', '5', '6', '7', '8', '9', '10', '11', '12', '1', '2', '3'];
  const QUARTER_PERIOD_OPTIONS = ['Q1', 'Q2', 'Q3', 'Q4'];

  const periodOptionsFor = (frequency) => {
    if (frequency === 'quarterly') return QUARTER_PERIOD_OPTIONS;
    if (frequency === 'monthly') return MONTH_PERIOD_OPTIONS;
    return [];
  };

  const periodLabel = (period) => (period == null ? 'Whole FY' : MONTH_LABELS[period] || period);

  const openPeriodModal = (row) => {
    setPeriodModalForm(row);
    setPeriodRuleForm({
      fy_year: selectedCompany?.fy_year ? String(selectedCompany.fy_year) : '',
      period: periodOptionsFor(row.frequency)[0] || '',
      applicability: 'Yes',
      remarks: '',
    });
    api
      .get(`/companies/${selectedCompanyId}/form-applicability/${encodeURIComponent(row.form_name)}/rules`)
      .then((res) => setPeriodRules(res.data));
  };

  const closePeriodModal = () => {
    setPeriodModalForm(null);
    setPeriodRules([]);
  };

  const doAddPeriodRule = () => {
    if (!periodModalForm || !periodRuleForm.fy_year) return;
    setPeriodSaving(true);
    api
      .put(`/companies/${selectedCompanyId}/form-applicability/${encodeURIComponent(periodModalForm.form_name)}`, {
        applicability: periodRuleForm.applicability,
        remarks: periodRuleForm.remarks || null,
        fy_year: Number(periodRuleForm.fy_year),
        period: periodRuleForm.period || null,
      })
      .then((res) => {
        setPeriodRules((prev) => [
          res.data,
          ...prev.filter((r) => !(r.fy_year === res.data.fy_year && r.period === res.data.period)),
        ]);
        refreshFormApplicability();
        setPeriodRuleForm((f) => ({ ...f, remarks: '' }));
        setToast('Rule saved ✓');
        setTimeout(() => setToast(null), 3000);
      })
      .finally(() => setPeriodSaving(false));
  };

  const addPeriodRule = (e) => {
    e.preventDefault();
    if (
      periodModalForm &&
      GST_MANDATORY_NIL_FORMS.includes(periodModalForm.form_name) &&
      periodRuleForm.applicability !== 'Yes'
    ) {
      setGstWarningPending({ proceed: doAddPeriodRule });
      return;
    }
    doAddPeriodRule();
  };

  const removePeriodRule = (ruleId) => {
    if (!periodModalForm) return;
    api
      .delete(`/companies/${selectedCompanyId}/form-applicability/${encodeURIComponent(periodModalForm.form_name)}/rules/${ruleId}`)
      .then(() => {
        setPeriodRules((prev) => prev.filter((r) => r.id !== ruleId));
        refreshFormApplicability();
      });
  };

  const refreshTdsSections = () => api.get('/tds-sections').then((res) => setTdsSections(res.data));

  useEffect(() => {
    refreshTdsSections();
  }, []);

  const openAddTds = () => {
    setEditingTdsId(null);
    setTdsForm(EMPTY_TDS_SECTION);
    setTdsModalOpen(true);
  };

  const openEditTds = (row) => {
    setEditingTdsId(row.id);
    setTdsForm({
      old_section: row.old_section || '',
      new_section: row.new_section || '',
      description: row.description || '',
      rate_percent: row.rate_percent ?? '',
      threshold_amount: row.threshold_amount ?? '',
      effective_from: row.effective_from || '',
      is_active: row.is_active,
      remarks: row.remarks || '',
    });
    setTdsModalOpen(true);
  };

  const handleTdsSubmit = (e) => {
    e.preventDefault();
    setTdsSaving(true);
    const payload = {
      ...tdsForm,
      rate_percent: tdsForm.rate_percent === '' ? null : Number(tdsForm.rate_percent),
      threshold_amount: tdsForm.threshold_amount === '' ? null : Number(tdsForm.threshold_amount),
      effective_from: tdsForm.effective_from || null,
    };
    const request = editingTdsId
      ? api.put(`/tds-sections/${editingTdsId}`, payload)
      : api.post('/tds-sections', payload);
    request
      .then(() => {
        refreshTdsSections();
        setToast(editingTdsId ? 'TDS section updated ✓' : 'TDS section added ✓');
        setTimeout(() => setToast(null), 3000);
        setTdsModalOpen(false);
      })
      .finally(() => setTdsSaving(false));
  };

  const toggleTdsActive = (row) => {
    api
      .put(`/tds-sections/${row.id}`, {
        old_section: row.old_section,
        new_section: row.new_section,
        description: row.description,
        rate_percent: row.rate_percent,
        threshold_amount: row.threshold_amount,
        effective_from: row.effective_from,
        is_active: !row.is_active,
        remarks: row.remarks,
      })
      .then(() => {
        refreshTdsSections();
        setToast(row.is_active ? `${row.new_section} deactivated` : `${row.new_section} activated`);
        setTimeout(() => setToast(null), 3000);
      });
  };

  const refreshLedgerMappings = () => {
    if (!selectedCompanyId || !isOwnerAdmin) return;
    api.get(`/companies/${selectedCompanyId}/tally-ledger-mappings`).then((res) => setLedgerMappings(res.data));
  };

  useEffect(() => {
    refreshLedgerMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, isOwnerAdmin]);

  const firstFieldForCategory = (category) => {
    if (category === 'gst') return Object.keys(GST_FIELD_LABELS)[0];
    if (category === 'financials') return Object.keys(FINANCIALS_FIELD_LABELS)[0];
    return tdsSections[0]?.new_section || '';
  };

  const mappingFieldLabel = (row) => {
    if (row.category === 'gst') return GST_FIELD_LABELS[row.mapped_field] || row.mapped_field;
    if (row.category === 'financials') return FINANCIALS_FIELD_LABELS[row.mapped_field] || row.mapped_field;
    const section = tdsSections.find((s) => s.new_section === row.mapped_field);
    return section ? `${section.new_section} - ${section.description || ''}`.trim().replace(/-\s*$/, '') : row.mapped_field;
  };

  const openAddLedgerMapping = () => {
    setEditingLedgerMappingId(null);
    setLedgerMappingForm(EMPTY_LEDGER_MAPPING);
    setLedgerMappingModalOpen(true);
  };

  const openEditLedgerMapping = (row) => {
    setEditingLedgerMappingId(row.id);
    setLedgerMappingForm({
      category: row.category, mapped_field: row.mapped_field,
      tally_ledger_name: row.tally_ledger_name, rate: row.rate ?? '',
    });
    setLedgerMappingModalOpen(true);
  };

  const handleLedgerMappingSubmit = (e) => {
    e.preventDefault();
    setLedgerMappingSaving(true);
    const payload = {
      category: ledgerMappingForm.category,
      mapped_field: ledgerMappingForm.mapped_field,
      tally_ledger_name: ledgerMappingForm.tally_ledger_name,
      rate: ledgerMappingForm.category === 'tds' && ledgerMappingForm.rate !== '' ? Number(ledgerMappingForm.rate) : null,
    };
    const request = editingLedgerMappingId
      ? api.put(`/companies/${selectedCompanyId}/tally-ledger-mappings/${editingLedgerMappingId}`, payload)
      : api.post(`/companies/${selectedCompanyId}/tally-ledger-mappings`, payload);
    request
      .then(() => {
        refreshLedgerMappings();
        setToast(editingLedgerMappingId ? 'Ledger mapping updated ✓' : 'Ledger mapping added ✓');
        setTimeout(() => setToast(null), 3000);
        setLedgerMappingModalOpen(false);
      })
      .catch((err) => {
        setToast(err.response?.data?.detail || 'Something went wrong');
        setTimeout(() => setToast(null), 4000);
      })
      .finally(() => setLedgerMappingSaving(false));
  };

  const confirmDeleteLedgerMapping = () => {
    if (!ledgerMappingDeleteTarget) return;
    api.delete(`/companies/${selectedCompanyId}/tally-ledger-mappings/${ledgerMappingDeleteTarget.id}`).then(() => {
      refreshLedgerMappings();
      setToast('Ledger mapping deleted');
      setTimeout(() => setToast(null), 3000);
      setLedgerMappingDeleteTarget(null);
    });
  };

  const refreshApiKeys = () => {
    if (!selectedCompanyId || !isOwnerAdmin) return;
    api.get(`/companies/${selectedCompanyId}/api-keys`).then((res) => setApiKeys(res.data));
  };

  useEffect(() => {
    refreshApiKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, isOwnerAdmin]);

  const openAddApiKey = () => {
    setApiKeyForm(EMPTY_API_KEY_FORM);
    setApiKeyModalOpen(true);
  };

  const handleApiKeySubmit = (e) => {
    e.preventDefault();
    setApiKeySaving(true);
    api
      .post(`/companies/${selectedCompanyId}/api-keys`, apiKeyForm)
      .then((res) => {
        refreshApiKeys();
        setApiKeyModalOpen(false);
        setNewApiKeyPlaintext(res.data);
      })
      .catch((err) => {
        setToast(err.response?.data?.detail || 'Something went wrong');
        setTimeout(() => setToast(null), 4000);
      })
      .finally(() => setApiKeySaving(false));
  };

  const confirmRevokeApiKey = () => {
    if (!apiKeyRevokeTarget) return;
    api.delete(`/companies/${selectedCompanyId}/api-keys/${apiKeyRevokeTarget.id}`).then(() => {
      refreshApiKeys();
      setToast(`"${apiKeyRevokeTarget.label}" revoked`);
      setTimeout(() => setToast(null), 3000);
      setApiKeyRevokeTarget(null);
    });
  };

  const copyApiKeyToClipboard = () => {
    if (!newApiKeyPlaintext) return;
    navigator.clipboard?.writeText(newApiKeyPlaintext.plaintext_key);
    setToast('Copied to clipboard');
    setTimeout(() => setToast(null), 2000);
  };

  const refreshUsers = () => {
    if (!canManageUsers) return;
    api.get('/users').then((res) => setUsers(res.data));
  };

  useEffect(() => {
    refreshUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageUsers]);

  const openAddUser = () => {
    setEditingUserId(null);
    setUserForm(EMPTY_USER_FORM);
    setUserModalOpen(true);
  };

  const openEditUser = (u) => {
    setEditingUserId(u.id);
    setUserForm({ name: u.name, email: u.email, password: '', role: u.role });
    setUserModalOpen(true);
  };

  const handleUserSubmit = (e) => {
    e.preventDefault();
    setUserSaving(true);
    const request = editingUserId
      ? api.put(`/users/${editingUserId}`, {
          name: userForm.name,
          role: userForm.role,
          password: userForm.password || undefined,
        })
      : api.post('/users', {
          ...userForm,
          // Ties the new user to the company being administered, so they show
          // up in this list (which is scoped by assignment) and can actually
          // see something when they log in.
          company_id: isCustomerOwner ? selectedCompanyId : undefined,
        });
    request
      .then(() => {
        refreshUsers();
        setToast(editingUserId ? 'User updated ✓' : 'User created ✓');
        setTimeout(() => setToast(null), 3000);
        setUserModalOpen(false);
      })
      .catch((err) => {
        setToast(err.response?.data?.detail || 'Something went wrong');
        setTimeout(() => setToast(null), 4000);
      })
      .finally(() => setUserSaving(false));
  };

  const toggleUserActive = (u) => {
    api.put(`/users/${u.id}`, { is_active: !u.is_active }).then(() => {
      refreshUsers();
      setToast(u.is_active ? `${u.name} deactivated` : `${u.name} activated`);
      setTimeout(() => setToast(null), 3000);
    });
  };

  const confirmDeleteUser = () => {
    if (!userDeleteTarget) return;
    api.delete(`/users/${userDeleteTarget.id}`).then(() => {
      refreshUsers();
      setToast(`${userDeleteTarget.name} deleted`);
      setTimeout(() => setToast(null), 3000);
      setUserDeleteTarget(null);
    });
  };

  const openAssignments = (u) => {
    setAssignmentsUser(u);
    setAssignmentForm({ company_id: '', client_id: '' });
    setAssignmentClients([]);
    setAssignmentClientNames({});
    api.get(`/users/${u.id}/assignments`).then((res) => {
      setUserAssignments(res.data);
      const companyIds = [...new Set(res.data.filter((a) => a.client_id).map((a) => a.company_id))];
      Promise.all(
        companyIds.map((cid) => api.get('/clients', { params: { company_id: cid } }).then((r) => r.data))
      ).then((lists) => {
        const map = {};
        lists.flat().forEach((c) => {
          map[c.id] = c.name_of_client;
        });
        setAssignmentClientNames(map);
      });
    });
  };

  const closeAssignments = () => {
    setAssignmentsUser(null);
    setUserAssignments([]);
    setAssignmentClientNames({});
  };

  const handleAssignmentCompanyChange = (companyId) => {
    setAssignmentForm({ company_id: companyId, client_id: '' });
    if (!companyId) {
      setAssignmentClients([]);
      return;
    }
    api.get('/clients', { params: { company_id: companyId } }).then((res) => setAssignmentClients(res.data));
  };

  const addAssignment = (e) => {
    e.preventDefault();
    if (!assignmentsUser || !assignmentForm.company_id) return;
    setAssignmentSaving(true);
    api
      .post(`/users/${assignmentsUser.id}/assignments`, {
        company_id: Number(assignmentForm.company_id),
        client_id: assignmentForm.client_id ? Number(assignmentForm.client_id) : null,
      })
      .then((res) => {
        setUserAssignments((prev) => [...prev, res.data]);
        if (res.data.client_id) {
          const client = assignmentClients.find((c) => c.id === res.data.client_id);
          if (client) {
            setAssignmentClientNames((prev) => ({ ...prev, [client.id]: client.name_of_client }));
          }
        }
        setAssignmentForm({ company_id: '', client_id: '' });
        setAssignmentClients([]);
      })
      .catch((err) => {
        setToast(err.response?.data?.detail || 'Could not add assignment');
        setTimeout(() => setToast(null), 4000);
      })
      .finally(() => setAssignmentSaving(false));
  };

  const removeAssignment = (assignmentId) => {
    if (!assignmentsUser) return;
    api.delete(`/users/${assignmentsUser.id}/assignments/${assignmentId}`).then(() => {
      setUserAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
    });
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || `Company #${id}`;

  useEffect(() => {
    if (selectedCompany) {
      setForm((f) => ({
        ...f,
        name: selectedCompany.name || '',
        cin: selectedCompany.cin || '',
        gstin: selectedCompany.gstin || '',
        pan: selectedCompany.pan || '',
        state: selectedCompany.state || '',
        email: selectedCompany.email || '',
        mobile: selectedCompany.mobile || '',
        client_mode: selectedCompany.client_mode || 'Professional',
      }));
    }
  }, [selectedCompany]);

  const handleSave = (e) => {
    e.preventDefault();
    if (!selectedCompany) return;
    setSaving(true);
    api
      // PUT /companies replaces every field on CompanyCreate, so anything
      // omitted here silently reverts to that schema's default - client_mode
      // and the rest must be sent explicitly, not left out.
      .put(`/companies/${selectedCompany.id}`, {
        name: form.name,
        cin: form.cin,
        gstin: form.gstin,
        pan: form.pan,
        state: form.state,
        email: form.email || null,
        mobile: form.mobile || null,
        company_type: selectedCompany.company_type,
        client_mode: form.client_mode,
        business_structure: selectedCompany.business_structure,
        fy_end_month: selectedCompany.fy_end_month,
        gst_filing_frequency: selectedCompany.gst_filing_frequency,
        registration_date: selectedCompany.registration_date,
      })
      .then(() => {
        refreshCompanies();
        setToast('Settings saved ✓');
        setTimeout(() => setToast(null), 3000);
      })
      .finally(() => setSaving(false));
  };

  const confirmRegenerate = () => {
    if (!regenTarget) return;
    const target = regenTarget;
    setRegeneratingId(target.id);
    setRegenTarget(null);
    api
      .post(`/companies/${target.id}/regenerate-compliance`)
      .then((res) => {
        const { removed, kept, total_after: total } = res.data;
        setToast(`Calendar rebuilt — ${total} items for FY ${res.data.fy_year}, ${kept} already-filed kept, ${removed} pending replaced`);
        setTimeout(() => setToast(null), 6000);
      })
      .catch((err) => {
        setToast(err.response?.data?.detail || 'Could not rebuild the calendar');
        setTimeout(() => setToast(null), 5000);
      })
      .finally(() => setRegeneratingId(null));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteCompany(deleteTarget.id).then(() => {
      setToast(`${deleteTarget.name} deleted`);
      setTimeout(() => setToast(null), 3000);
      setDeleteTarget(null);
    });
  };

  return (
    <PageLayout>
      <div className="page-title-row">
        <h1 className="page-title">Settings</h1>
      </div>

      {toast && <div className="cal-toast">{toast}</div>}

      <div className="settings-tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`settings-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
            type="button"
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'Company Profile' && (
        <form className="dash-card" onSubmit={handleSave}>
          <h2 className="dash-card-title">Company Profile</h2>
          <div className="field-row">
            <div className="field-group">
              <span className="field-label">Company Name</span>
              <input
                className="field-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="field-group">
              <span className="field-label">CIN</span>
              <input
                className="field-input"
                value={form.cin}
                onChange={(e) => setForm({ ...form, cin: e.target.value })}
              />
            </div>
            <div className="field-group">
              <span className="field-label">GSTIN</span>
              <input
                className="field-input"
                value={form.gstin}
                onChange={(e) => setForm({ ...form, gstin: e.target.value })}
              />
              <GstinLookup
                gstin={form.gstin}
                variant="button"
                onFetched={(data) =>
                  setForm((f) => ({
                    ...f,
                    name: data.legal_name || data.trade_name || f.name,
                    state: data.state || f.state,
                  }))
                }
              />
            </div>
            <div className="field-group">
              <span className="field-label">PAN</span>
              <input
                className="field-input"
                value={form.pan}
                onChange={(e) => setForm({ ...form, pan: e.target.value })}
              />
            </div>
            <div className="field-group">
              <span className="field-label">IT Ward / Circle</span>
              <input
                className="field-input"
                value={form.itWard}
                onChange={(e) => setForm({ ...form, itWard: e.target.value })}
                placeholder="e.g. Ward 12(3), Delhi"
              />
            </div>
            <div className="field-group">
              <span className="field-label">State</span>
              <select
                className="field-select"
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
              >
                <option value="">Select state</option>
                {INDIA_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="field-group">
              <span className="field-label">Audit Type</span>
              <select
                className="field-select"
                value={form.auditType}
                onChange={(e) => setForm({ ...form, auditType: e.target.value })}
              >
                <option value="Tax Audit u/s 44AB">Tax Audit u/s 44AB</option>
                <option value="Non-Audit">Non-Audit</option>
              </select>
            </div>
            <div className="field-group">
              <span className="field-label">Email</span>
              <input
                className="field-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="field-group">
              <span className="field-label">Mobile</span>
              <input
                className="field-input"
                type="tel"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              />
            </div>
          </div>

          <div className="field-group" style={{ maxWidth: 520 }}>
            <span className="field-label">Usage Type</span>
            <select
              className="field-select"
              value={form.client_mode}
              onChange={(e) => setForm({ ...form, client_mode: e.target.value })}
            >
              <option value="Professional">Professional — I manage multiple clients (shows Client Master)</option>
              <option value="Business Owner">Business Owner — I track only this company</option>
            </select>
            <p className="dash-table-sub" style={{ marginTop: 6, marginBottom: 0 }}>
              This is what controls whether <strong>Client Master</strong> appears in the sidebar. Signup
              defaults to Business Owner; switch to Professional if you manage books for several clients.
              Nothing is deleted either way — the menu and the client field on ITR/Audit/DSC/Documents
              simply show or hide.
            </p>
          </div>
          <div className="dash-btn-row">
            <button className="dash-btn dash-btn-orange" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}

      {activeTab === 'Manage Companies' && (
        <div className="dash-card">
          <div className="page-title-row">
            <h2 className="dash-card-title">Your Companies</h2>
            <button className="dash-btn dash-btn-orange" type="button" onClick={openAddModal}>
              + Add New Company
            </button>
          </div>

          <table className="dash-table">
            <thead>
              <tr>
                <th>Company Name</th>
                <th>GSTIN</th>
                <th>State</th>
                <th>Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={6}>No companies yet.</td>
                </tr>
              )}
              {companies.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="company-name-cell">
                      <span>{c.name}</span>
                      {c.id === selectedCompanyId && (
                        <span className="dash-pill dash-pill-green">Active</span>
                      )}
                    </div>
                  </td>
                  <td>{c.gstin || '—'}</td>
                  <td>{c.state || '—'}</td>
                  <td>
                    {c.company_type ? (
                      COMPANY_TYPE_LABELS[c.company_type] || c.company_type
                    ) : (
                      <span className="dash-pill dash-pill-amber" title="Company type decides which ROC forms apply - set it via Edit">
                        Not set
                      </span>
                    )}
                  </td>
                  <td>
                    {c.id === selectedCompanyId ? (
                      <span className="dash-pill dash-pill-green">Active</span>
                    ) : (
                      <span className="dash-pill dash-pill-blue">Inactive</span>
                    )}
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEditModal(c)}>
                        Edit
                      </button>
                      {c.id !== selectedCompanyId && (
                        <button
                          className="dash-btn dash-btn-teal"
                          type="button"
                          onClick={() => selectCompany(c.id)}
                        >
                          Switch
                        </button>
                      )}
                      <button
                        className="dash-btn cal-btn-grey"
                        type="button"
                        disabled={regeneratingId === c.id}
                        title="Rebuild this company's compliance calendar from its current company type. Filed items are never touched."
                        onClick={() => setRegenTarget(c)}
                      >
                        {regeneratingId === c.id ? 'Rebuilding...' : 'Rebuild Calendar'}
                      </button>
                      <button
                        className="dash-btn-red-outline"
                        type="button"
                        onClick={() => setDeleteTarget(c)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'Compliance Applicability' && (
        <div className="dash-card">
          <h2 className="dash-card-title">Compliance Applicability — {selectedCompany?.name || ''}</h2>
          <p className="dash-table-sub" style={{ marginBottom: 16 }}>
            Mark a compliance category as Not Applicable if it genuinely doesn't apply to this
            company (e.g. no employees means PF/ESIC don't apply yet). It stays visible on the
            Compliance Calendar, greyed out and labelled N/A, and is excluded from the Dashboard's
            Action Required list and penalty exposure. Reversible any time.
          </p>
          {categorySettings.map((row) => (
            <div className="toggle-row" key={row.category}>
              <span className="toggle-row-label">{CATEGORY_LABELS[row.category] || row.category}</span>
              <button
                type="button"
                className={`dash-pill ${row.is_applicable ? 'dash-pill-green' : 'dash-pill-blue'}`}
                style={{ border: 'none', cursor: 'pointer' }}
                disabled={togglingCategory === row.category}
                onClick={() => toggleCategoryApplicability(row)}
              >
                {togglingCategory === row.category ? 'Updating...' : row.is_applicable ? 'Applicable' : 'N/A — click to re-enable'}
              </button>
            </div>
          ))}

          <h2 className="dash-card-title" style={{ marginTop: 28 }}>Form-Level Applicability</h2>
          <p className="dash-table-sub" style={{ marginBottom: 16 }}>
            More granular than the categories above — mark an individual form as No or NA (e.g.
            GSTR-9C only above the audit turnover threshold, or one TDS return sub-type if you have
            no salaried employees). Same effect as category N/A: stays visible on the Compliance
            Calendar, greyed and labelled, excluded from Dashboard numbers. Reversible any time. The
            dropdown below sets the whole-company default; for a monthly or quarterly form, use
            "Periods" to make it applicable (or not) starting from a specific month/quarter onward —
            e.g. "24Q applicable from Q2 FY 2026-27 onward" — without touching every future year.
          </p>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Form</th>
                <th>Applicability</th>
                <th>Remarks</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {formApplicability.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={5}>No forms found for this company type.</td>
                </tr>
              )}
              {formApplicability.map((row) => (
                <tr key={row.form_name}>
                  <td>{CATEGORY_LABELS[row.category] || row.category}</td>
                  <td>{row.form_name}</td>
                  <td>
                    <select
                      className="field-select"
                      style={{ minWidth: 110 }}
                      value={row.applicability}
                      disabled={savingForm === row.form_name}
                      onChange={(e) => updateFormApplicability(row, e.target.value)}
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="NA">NA</option>
                    </select>
                  </td>
                  <td>
                    <div className="table-actions">
                      <input
                        className="field-input"
                        style={{ minWidth: 160 }}
                        placeholder="Optional remarks"
                        value={formRemarksDraft[row.form_name] ?? ''}
                        onChange={(e) =>
                          setFormRemarksDraft((prev) => ({ ...prev, [row.form_name]: e.target.value }))
                        }
                      />
                      <button
                        className="dash-btn cal-btn-grey"
                        type="button"
                        disabled={savingForm === row.form_name}
                        onClick={() => saveFormRemarks(row)}
                      >
                        Save
                      </button>
                    </div>
                  </td>
                  <td>
                    {row.frequency !== 'annually' && (
                      <button className="dash-btn cal-btn-grey" type="button" onClick={() => openPeriodModal(row)}>
                        Periods
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'TDS Sections' && (
        <div className="dash-card">
          <div className="page-title-row">
            <h2 className="dash-card-title">TDS Sections</h2>
            {isOwnerAdmin && (
              <button className="dash-btn dash-btn-orange" type="button" onClick={openAddTds}>
                + Add New Section
              </button>
            )}
          </div>
          <p className="dash-table-sub" style={{ marginBottom: 16 }}>
            {isOwnerAdmin ? (
              <>
                Statutory rate master, shared by every company on the platform — editing a rate here
                changes it for all of them. Both the old Income-tax Act codes (194C, 194J…) and the
                Income-tax Act 2025 codes (393(1)-6(i)…, effective 01/04/2026) are listed. Deactivate the
                old-act rows once you've migrated; rows already recorded against them keep working.
              </>
            ) : (
              <>
                Reference list of TDS sections and rates, shown here so you can look up the right section
                while entering data. These are statutory rates maintained centrally by Krosifin and shared
                across all companies, so they're read-only. Both the old Income-tax Act codes and the
                Income-tax Act 2025 codes (effective 01/04/2026) are shown. If a rate looks wrong, call{' '}
                {SUPPORT.phone}.
              </>
            )}
          </p>

          <table className="dash-table">
            <thead>
              <tr>
                <th>Old Section</th>
                <th>New Section</th>
                <th>Description</th>
                <th>Rate %</th>
                <th>Threshold ₹</th>
                <th>Effective From</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tdsSections.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={8}>No TDS sections yet.</td>
                </tr>
              )}
              {tdsSections.map((row) => (
                <tr key={row.id}>
                  <td>{row.old_section || '—'}</td>
                  <td>{row.new_section || '—'}</td>
                  <td>{row.description || '—'}</td>
                  <td>{row.rate_percent === null ? 'Slab' : `${row.rate_percent}%`}</td>
                  <td>{row.threshold_amount ? `₹${Number(row.threshold_amount).toLocaleString('en-IN')}` : '—'}</td>
                  <td>{row.effective_from || '—'}</td>
                  <td>
                    <span className={`dash-pill ${row.is_active ? 'dash-pill-green' : 'dash-pill-blue'}`}>
                      {row.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {isOwnerAdmin ? (
                      <div className="table-actions">
                        <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEditTds(row)}>
                          Edit
                        </button>
                        <button
                          className={row.is_active ? 'dash-btn-red-outline' : 'dash-btn dash-btn-teal'}
                          type="button"
                          onClick={() => toggleTdsActive(row)}
                        >
                          {row.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    ) : (
                      <span className="dash-table-sub">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'Tally Ledger Mapping' && plan && !plan.has_pro_access && (
        <div className="dash-card">
          <h2 className="dash-card-title">Tally Connector is a Pro feature</h2>
          <p className="dash-table-sub">
            The Tally Connector needs the Pro plan ({inr(plan.pro_total_inr)}/month incl. GST). Open{' '}
            <button type="button" className="setup-link-btn" onClick={() => setActiveTab('Plan & Billing')}>
              Plan &amp; Billing
            </button>{' '}
            to upgrade, or call {SUPPORT.phone}. Existing mappings are shown below and are kept safe —
            they simply stop syncing until Pro is active.
          </p>
        </div>
      )}

      {activeTab === 'Tally Ledger Mapping' && (
        <div className="dash-card">
          <div className="page-title-row">
            <h2 className="dash-card-title">Tally Ledger Mapping — {selectedCompany?.name || ''}</h2>
            <div className="table-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setLedgerMappingImportOpen(true)}>
                Import
              </button>
              <button className="dash-btn dash-btn-orange" type="button" onClick={openAddLedgerMapping}>
                + Add Ledger Mapping
              </button>
            </div>
          </div>
          <p className="dash-table-sub" style={{ marginBottom: 16 }}>
            Map your actual Tally ledger names to the Krosifin fields the Tally Connector syncs from -
            GST, TDS, or Financials. A field can have more than one ledger mapped to it (e.g. separate
            Output CGST ledgers per rate slab, or multiple TDS sections at different rates) — the
            connector sums every ledger mapped to a field.
          </p>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Krosifin Field</th>
                <th>Rate</th>
                <th>Tally Ledger Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ledgerMappings.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={5}>No ledger mappings yet.</td>
                </tr>
              )}
              {ledgerMappings.map((row) => (
                <tr key={row.id}>
                  <td>{MAPPING_CATEGORY_LABELS[row.category] || row.category}</td>
                  <td>{mappingFieldLabel(row)}</td>
                  <td>{row.rate != null ? `${row.rate}%` : '—'}</td>
                  <td>{row.tally_ledger_name}</td>
                  <td>
                    <div className="table-actions">
                      <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEditLedgerMapping(row)}>
                        Edit
                      </button>
                      <button className="dash-btn-red-outline" type="button" onClick={() => setLedgerMappingDeleteTarget(row)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'API Keys' && plan && !plan.has_pro_access && (
        <div className="dash-card">
          <h2 className="dash-card-title">API Keys are a Pro feature</h2>
          <p className="dash-table-sub">
            Generating API keys needs the Pro plan ({inr(plan.pro_total_inr)}/month incl. GST). Open{' '}
            <button type="button" className="setup-link-btn" onClick={() => setActiveTab('Plan & Billing')}>
              Plan &amp; Billing
            </button>{' '}
            to upgrade, or call {SUPPORT.phone}. You can still revoke existing keys below at any time —
            that stays available so you're never locked out of a security action.
          </p>
        </div>
      )}

      {activeTab === 'API Keys' && (
        <div className="dash-card">
          <div className="page-title-row">
            <h2 className="dash-card-title">API Keys — {selectedCompany?.name || ''}</h2>
            <button className="dash-btn dash-btn-orange" type="button" onClick={openAddApiKey}>
              + Generate New Key
            </button>
          </div>
          <p className="dash-table-sub" style={{ marginBottom: 16 }}>
            API keys authenticate the standalone Tally Connector tool for this company — separate from
            your login, and shown only once at creation, so copy the key somewhere safe right away.
            Revoking a key immediately blocks any connector install using it.
          </p>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Key</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last Used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={6}>No API keys yet.</td>
                </tr>
              )}
              {apiKeys.map((k) => (
                <tr key={k.id}>
                  <td>{k.label}</td>
                  <td><code>{k.key_prefix}…</code></td>
                  <td>
                    <span className={`dash-pill ${k.is_active ? 'dash-pill-green' : 'dash-pill-blue'}`}>
                      {k.is_active ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td>{new Date(k.created_at).toLocaleDateString('en-IN')}</td>
                  <td>{k.last_used_at ? new Date(k.last_used_at).toLocaleString('en-IN') : 'Never'}</td>
                  <td>
                    {k.is_active && (
                      <div className="table-actions">
                        <button className="dash-btn-red-outline" type="button" onClick={() => setApiKeyRevokeTarget(k)}>
                          Revoke
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'Tally Connector' && (
        <>
          {plan && !plan.has_pro_access && (
            <div className="dash-card">
              <h2 className="dash-card-title">The Tally Connector is a Pro feature</h2>
              <p className="dash-table-sub">
                Syncing from Tally needs the Pro plan ({inr(plan.pro_total_inr)}/month incl. GST). Open{' '}
                <button type="button" className="setup-link-btn" onClick={() => setActiveTab('Plan & Billing')}>
                  Plan &amp; Billing
                </button>{' '}
                to upgrade, or call {SUPPORT.phone}. The steps below are here so you can see what's
                involved before deciding.
              </p>
            </div>
          )}

          <div className="dash-card">
            <h2 className="dash-card-title">Tally Connector — {selectedCompany?.name || ''}</h2>
            <p className="dash-table-sub" style={{ marginBottom: 18 }}>
              A small Windows program that runs on the machine where Tally is installed. It reads the
              figures you've mapped, shows them to you for confirmation, and only then sends them to
              Krosifin. It never writes anything back into Tally, and Tally must be open while it runs.
            </p>

            {/* The order matters and is not guessable: skip step 2 and the
                connector fails with "No ledger mappings found", which reads
                like a broken download rather than a missing setup step. */}
            <ol className="connector-steps">
              <li>
                <strong>Generate an API key for this company.</strong> Go to{' '}
                <button type="button" className="setup-link-btn" onClick={() => setActiveTab('API Keys')}>
                  API Keys
                </button>{' '}
                and create one. It is shown only once — copy it somewhere safe immediately. The key is
                what ties a connector install to this company, so use a separate key per company.
              </li>
              <li>
                <strong>Map your Tally ledgers.</strong> Open{' '}
                <button
                  type="button"
                  className="setup-link-btn"
                  onClick={() => setActiveTab('Tally Ledger Mapping')}
                >
                  Tally Ledger Mapping
                </button>{' '}
                and tell Krosifin which ledger in Tally corresponds to which figure. <strong>Do this
                before installing</strong> — without it the connector has nothing to read and will
                stop with "No ledger mappings found".
              </li>
              <li>
                <strong>Download and run the connector</strong> on the computer that has Tally. There
                is no installer and it needs no admin rights — save the file somewhere permanent (not
                Downloads) and double-click it. It creates a <code>configs</code> folder beside itself
                to remember your companies.
              </li>
              <li>
                <strong>Add the company inside the connector.</strong> Click <em>Add Company</em>, enter
                the company name and the API key from step 1, and leave the server address box exactly
                as it is. Change the Tally port only if your Tally runs on something other than 9000
                (Gateway of Tally → F1 Help → Settings → Connectivity).
              </li>
            </ol>

            <div className="connector-download-row">
              <a className="dash-btn dash-btn-orange" href={CONNECTOR_DOWNLOAD_URL}>
                Download Tally Connector (Windows)
              </a>
              <span className="dash-table-sub">About 11 MB · Windows 10 or 11 · no Python needed</span>
            </div>
          </div>

          <div className="dash-card">
            <h2 className="dash-card-title">If Windows blocks the download</h2>
            <p className="dash-table-sub">
              Windows shows <strong>"Windows protected your PC"</strong> for programs it has not seen
              often yet, which includes this one. It is not a virus warning and nothing is wrong with
              the file. Click <strong>More info</strong>, then <strong>Run anyway</strong>. Your browser
              may also ask you to confirm the download first — choose <strong>Keep</strong>.
            </p>
            <p className="dash-table-sub" style={{ marginTop: 10 }}>
              If your office IT blocks it entirely, the file is served from GitHub at the link above and
              can be allow-listed. Call {SUPPORT.phone} if you get stuck.
            </p>
          </div>

          <div className="dash-card">
            <h2 className="dash-card-title">Common problems</h2>
            <table className="dash-table">
              <thead>
                <tr>
                  <th>What you see</th>
                  <th>What it means</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>"No ledger mappings found"</td>
                  <td>Step 2 was skipped for this company. Set up Tally Ledger Mapping, then retry.</td>
                </tr>
                <tr>
                  <td>"Could not reach Tally"</td>
                  <td>
                    Tally is closed, or it is running on a different port. Open Tally and check Gateway
                    of Tally → F1 Help → Settings → Connectivity.
                  </td>
                </tr>
                <tr>
                  <td>Unauthorised / invalid key</td>
                  <td>
                    The key was revoked or belongs to another company. Generate a fresh one under API
                    Keys and add the company again in the connector.
                  </td>
                </tr>
                <tr>
                  <td>Figures look wrong</td>
                  <td>
                    The connector shows every figure for confirmation before sending. If a number is
                    off, the ledger mapping is pointing at the wrong ledger — fix the mapping rather
                    than editing the figure afterwards.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'Import History' && (
        <div className="dash-card">
          <h2 className="dash-card-title">Import History — {selectedCompany?.name || ''}</h2>
          <p className="dash-table-sub" style={{ marginBottom: 16 }}>
            Every bulk import run for this company, with a row-level audit trail. Deleting a batch
            removes (or, for GST/TDS/ROC, reverts to unfiled) only the records that batch created and
            that haven't been edited since — anything you've since corrected by hand is left alone and
            called out before anything is deleted.
          </p>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Module</th>
                <th>File</th>
                <th>Date</th>
                <th>Imported</th>
                <th>Updated</th>
                <th>Duplicates</th>
                <th>Failed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {importHistory.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={8}>No imports yet for this company.</td>
                </tr>
              )}
              {importHistory.map((h) => (
                <tr key={h.id}>
                  <td>{IMPORT_MODULE_LABELS[h.module] || h.module}</td>
                  <td>{h.file_name}</td>
                  <td>{new Date(h.imported_at).toLocaleString('en-IN')}</td>
                  <td className="dash-serif">{h.success_count}</td>
                  <td className="dash-serif">{h.updated_count}</td>
                  <td className="dash-serif">{h.duplicate_count}</td>
                  <td className="dash-serif">{h.failure_count}</td>
                  <td>
                    <button
                      className="dash-btn-red-outline"
                      type="button"
                      disabled={previewLoading}
                      onClick={() => openDeletePreview(h)}
                    >
                      {['gst', 'roc', 'tds'].includes(h.module) ? 'Revert This Batch' : 'Delete This Batch'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'Subscribers' && isOwnerAdmin && (() => {
        const now = Date.now();
        const daysTo = (d) => (d ? Math.ceil((new Date(d).getTime() - now) / 86400000) : null);
        const bucket = (s) => {
          if (s.plan === 'pro' && s.has_pro_access) return 'paid';
          if (s.trial_active) return 'trial';
          return 'expired';
        };
        const filtered = subscribers.filter((s) => {
          if (subFilter === 'all') return true;
          if (subFilter === 'expiring') {
            const d = daysTo(s.trial_ends_at) ?? daysTo(s.plan_expires_at);
            return s.has_pro_access && d !== null && d <= 7;
          }
          return bucket(s) === subFilter;
        });
        const count = (b) => subscribers.filter((s) => bucket(s) === b).length;
        return (
          <>
            <div className="dash-stat-grid dash-stat-grid-3">
              <div className="dash-stat-card">
                <div className="dash-stat-value dash-serif green">{count('paid')}</div>
                <div className="dash-stat-label">Paying (Pro)</div>
              </div>
              <div className="dash-stat-card">
                <div className="dash-stat-value dash-serif">{count('trial')}</div>
                <div className="dash-stat-label">On free trial</div>
              </div>
              <div className="dash-stat-card">
                <div className={`dash-stat-value dash-serif ${count('expired') ? 'red' : 'grey'}`}>{count('expired')}</div>
                <div className="dash-stat-label">Free / lapsed</div>
              </div>
            </div>

            <div className="dash-card">
              <div className="page-title-row">
                <h2 className="dash-card-title">Subscribers ({subscribers.length})</h2>
                <div className="table-actions">
                  {['all', 'trial', 'paid', 'expired', 'expiring'].map((f) => (
                    <button
                      key={f}
                      type="button"
                      className={`dash-pill ${subFilter === f ? 'dash-pill-green' : 'dash-pill-blue'}`}
                      style={{ border: 'none', cursor: 'pointer' }}
                      onClick={() => setSubFilter(f)}
                    >
                      {f === 'expiring' ? 'Expiring ≤7d' : f[0].toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <p className="dash-table-sub" style={{ marginBottom: 16 }}>
                Every company on the platform. Payment arrives offline by UPI, so match the reference on
                your bank credit to the <strong>Ref</strong> column below, then use Activate Pro to grant
                access and record what was paid.
              </p>

              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Ref</th>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>Plan</th>
                    <th>Expires / trial left</th>
                    <th>Last payment</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td className="dash-empty" colSpan={7}>No companies in this view.</td></tr>
                  )}
                  {filtered.map((s) => {
                    const b = bucket(s);
                    return (
                      <tr key={s.company_id}>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{s.payment_reference}</td>
                        <td>
                          <strong>{s.company_name}</strong>
                          <div className="dash-table-sub">
                            joined {new Date(s.signed_up_at).toLocaleDateString('en-IN')}
                          </div>
                        </td>
                        <td>
                          <div>{s.contact_name || '—'}</div>
                          <div className="dash-table-sub">{s.contact_email || '—'}</div>
                          {s.contact_mobile && <div className="dash-table-sub">📞 {s.contact_mobile}</div>}
                        </td>
                        <td>
                          <span className={`dash-pill ${b === 'paid' ? 'dash-pill-green' : b === 'trial' ? 'dash-pill-amber' : 'dash-pill-blue'}`}>
                            {b === 'paid' ? 'Pro' : b === 'trial' ? 'Trial' : 'Free'}
                          </span>
                        </td>
                        <td>
                          {s.trial_active
                            ? `${s.trial_days_left} day${s.trial_days_left === 1 ? '' : 's'} of trial`
                            : s.plan_expires_at
                            ? new Date(s.plan_expires_at).toLocaleDateString('en-IN')
                            : s.plan === 'pro'
                            ? 'No expiry'
                            : '—'}
                        </td>
                        <td>
                          {s.last_payment_at ? (
                            <>
                              <div>{s.last_payment_amount_inr ? inr(s.last_payment_amount_inr) : '—'}</div>
                              <div className="dash-table-sub">
                                {new Date(s.last_payment_at).toLocaleDateString('en-IN')}
                                {s.last_payment_reference ? ` · ${s.last_payment_reference}` : ''}
                              </div>
                            </>
                          ) : (
                            <span className="dash-table-sub">Never</span>
                          )}
                        </td>
                        <td>
                          <div className="ncf-row-actions">
                            <button
                              className="dash-btn dash-btn-orange"
                              type="button"
                              onClick={() => {
                                setActivateForm({ months: 1, payment_reference: '', notes: '' });
                                setActivateTarget(s);
                              }}
                            >
                              Activate Pro
                            </button>
                            <button
                              className="dash-btn-red-outline"
                              type="button"
                              onClick={() => openSubscriberDelete(s)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        );
      })()}

      {subDeleteTarget && (
        <div className="ncf-modal-overlay" onClick={closeSubscriberDelete}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete {subDeleteTarget.company_name}</h3>

            {!subDeletePreview ? (
              <p className="dash-table-sub">Checking what this would remove…</p>
            ) : (
              <>
                <p>
                  This permanently deletes the company, everything filed or recorded under it, and
                  the accounts that exist only for it. <strong>It cannot be undone.</strong>
                </p>

                {subDeletePreview.users_deleted.length > 0 && (
                  <div className="ncf-modal-section">
                    <strong>User accounts deleted ({subDeletePreview.users_deleted.length})</strong>
                    <ul className="ncf-modal-list">
                      {subDeletePreview.users_deleted.map((u) => (
                        <li key={u.id}>
                          {u.name} — {u.email}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {subDeletePreview.users_kept.length > 0 && (
                  <div className="ncf-modal-section">
                    <strong>Kept ({subDeletePreview.users_kept.length})</strong>
                    <div className="dash-table-sub">
                      These belong to other companies too, so they keep their account and only lose
                      access to this one.
                    </div>
                    <ul className="ncf-modal-list">
                      {subDeletePreview.users_kept.map((u) => (
                        <li key={u.id}>
                          {u.name} — {u.email}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="ncf-modal-section">
                  <strong>Data deleted ({subDeletePreview.total_records} records)</strong>
                  {subDeletePreview.record_counts.length === 0 ? (
                    <div className="dash-table-sub">
                      Nothing has been entered under this company — it looks like an unused account.
                    </div>
                  ) : (
                    <ul className="ncf-modal-list">
                      {subDeletePreview.record_counts.map((r) => (
                        <li key={r.label}>
                          {r.label}: {r.count}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Typing the name is deliberate friction: these rows sit one
                    click apart in a list of near-identical "ABC" companies,
                    and a mis-click here is unrecoverable. */}
                <div className="field-group">
                  <span className="field-label">
                    Type <strong>{subDeleteTarget.company_name}</strong> to confirm
                  </span>
                  <input
                    className="field-input"
                    value={subDeleteConfirmText}
                    autoFocus
                    onChange={(e) => setSubDeleteConfirmText(e.target.value)}
                    placeholder={subDeleteTarget.company_name}
                  />
                </div>
              </>
            )}

            <div className="ncf-modal-actions">
              <button
                className="dash-btn cal-btn-grey"
                type="button"
                onClick={closeSubscriberDelete}
                disabled={subDeleting}
              >
                Cancel
              </button>
              <button
                className="dash-btn dash-btn-orange"
                style={{ background: '#dc2626' }}
                type="button"
                disabled={
                  subDeleting ||
                  !subDeletePreview ||
                  subDeleteConfirmText.trim() !== subDeleteTarget.company_name.trim()
                }
                onClick={confirmSubscriberDelete}
              >
                {subDeleting ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activateTarget && (
        <div className="ncf-modal-overlay" onClick={() => setActivateTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Activate Pro — {activateTarget.company_name}</h3>
            <p className="dash-table-sub" style={{ marginBottom: 16 }}>
              Payment reference for this company is{' '}
              <strong style={{ fontFamily: 'monospace' }}>{activateTarget.payment_reference}</strong> — it
              should appear on the UPI credit in your bank statement.
            </p>
            <div className="field-group">
              <span className="field-label">Period paid for</span>
              <select
                className="field-select"
                value={activateForm.months}
                onChange={(e) => setActivateForm({ ...activateForm, months: e.target.value })}
              >
                {(plan?.billing_options || [{ months: 1, total_inr: 1180 }, { months: 12, total_inr: 14160 }]).map((o) => (
                  <option key={o.months} value={o.months}>
                    {o.months === 1 ? '1 month' : `${o.months} months`} — {inr(o.total_inr)}
                  </option>
                ))}
              </select>
              {activateTarget.plan_expires_at && (
                <p className="dash-table-sub" style={{ marginTop: 6, marginBottom: 0 }}>
                  Extends from the current expiry ({new Date(activateTarget.plan_expires_at).toLocaleDateString('en-IN')}),
                  so an early renewal doesn't lose the remaining days.
                </p>
              )}
            </div>
            <div className="field-group">
              <span className="field-label">Bank / UPI reference (UTR)</span>
              <input
                className="field-input"
                placeholder="e.g. 512345678901"
                value={activateForm.payment_reference}
                onChange={(e) => setActivateForm({ ...activateForm, payment_reference: e.target.value })}
              />
            </div>
            <div className="field-group">
              <span className="field-label">Notes</span>
              <input
                className="field-input"
                value={activateForm.notes}
                onChange={(e) => setActivateForm({ ...activateForm, notes: e.target.value })}
              />
            </div>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setActivateTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" type="button" disabled={activating} onClick={submitActivation}>
                {activating ? 'Activating...' : 'Activate Pro'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'Plan & Billing' && plan && (
        <>
          <div className="dash-card">
            <h2 className="dash-card-title">Plan &amp; Billing — {selectedCompany?.name || ''}</h2>

            <div className="toggle-row">
              <span className="toggle-row-label">Current plan</span>
              <span className={`dash-pill ${plan.has_pro_access ? 'dash-pill-green' : 'dash-pill-blue'}`}>
                {plan.plan === 'pro' ? 'Pro' : plan.trial_active ? 'Free trial' : 'Free'}
              </span>
            </div>

            {plan.trial_active && (
              <p className="dash-table-sub" style={{ marginTop: 8 }}>
                <strong>{plan.trial_days_left} day{plan.trial_days_left === 1 ? '' : 's'} left</strong> in your
                free trial — you currently have full Pro access. When it ends you keep everything except{' '}
                {plan.pro_only_features.join(' and ')}; nothing is deleted and no data is lost.
              </p>
            )}
            {!plan.trial_active && plan.plan !== 'pro' && (
              <p className="dash-table-sub" style={{ marginTop: 8 }}>
                You're on the Free plan. All compliance tracking, GST/TDS/ROC, imports and alerts stay
                available — {plan.pro_only_features.join(' and ')} need Pro.
              </p>
            )}
            {plan.plan === 'pro' && (
              <p className="dash-table-sub" style={{ marginTop: 8 }}>
                Pro is active{plan.plan_expires_at ? ` until ${new Date(plan.plan_expires_at).toLocaleDateString('en-IN')}` : ''}. Thank you.
              </p>
            )}
          </div>

          <div className="dash-card">
            <h2 className="dash-card-title">Plans</h2>
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free</th>
                  <th>Pro — {inr(plan.pro_total_inr)}/month</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Compliance calendar, GST / TDS / ROC tracking</td>
                  <td className="green">✓</td>
                  <td className="green">✓</td>
                </tr>
                <tr>
                  <td>Excel import, alerts, users, custom compliances</td>
                  <td className="green">✓</td>
                  <td className="green">✓</td>
                </tr>
                <tr>
                  <td>Tally Connector</td>
                  <td>—</td>
                  <td className="green">✓</td>
                </tr>
                <tr>
                  <td>API Keys</td>
                  <td>—</td>
                  <td className="green">✓</td>
                </tr>
                <tr className="dash-table-total">
                  <td>Price per month</td>
                  <td>{inr(0)}</td>
                  <td>
                    {inr(plan.pro_price_inr)} + {inr(plan.pro_gst_amount_inr)} GST
                    ({plan.pro_gst_percent}%) = <strong>{inr(plan.pro_total_inr)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Shown even when already on Pro - that is how a customer renews or
              pays for another 12 months. */}
          {(() => {
            const selected =
              (plan.billing_options || []).find((o) => o.months === billingMonths) ||
              (plan.billing_options || [])[0];
            if (!selected) return null;
            return (
            <div className="dash-card">
              <h2 className="dash-card-title">
                {plan.has_pro_access ? 'Renew / extend Pro' : 'Upgrade to Pro'} — {inr(selected.total_inr)}
              </h2>

              <div className="field-group" style={{ maxWidth: 420, marginBottom: 12 }}>
                <span className="field-label">Billing period</span>
                <select
                  className="field-select"
                  value={billingMonths}
                  onChange={(e) => setBillingMonths(Number(e.target.value))}
                >
                  {(plan.billing_options || []).map((o) => (
                    <option key={o.months} value={o.months}>
                      {o.months === 1 ? '1 month' : `${o.months} months`} — {inr(o.total_inr)}
                    </option>
                  ))}
                </select>
              </div>

              <p className="dash-table-sub" style={{ marginBottom: 16 }}>
                {inr(selected.base_inr)} + {inr(selected.gst_inr)} GST ({plan.pro_gst_percent}%) ={' '}
                <strong>{inr(selected.total_inr)}</strong>
                {selected.months > 1 && ` (${inr(plan.pro_total_inr)} × ${selected.months} months)`}. Scan the
                QR — the amount is already filled in — or pay to the UPI ID below, then send us the payment
                reference. We activate Pro usually the same working day.
              </p>

              <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ textAlign: 'center' }}>
                  <img
                    key={selected.months}
                    src={`${BASE_URL}/api/payment-qr.png?months=${selected.months}&company_id=${selectedCompanyId}`}
                    alt={`UPI QR code to pay ${inr(selected.total_inr)} to ${SUPPORT.company}`}
                    style={{ width: 210, height: 210, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}
                  />
                  <p className="dash-table-sub" style={{ margin: '8px 0 0', fontSize: 12 }}>
                    Scan with any UPI app — pays {inr(selected.total_inr)}
                  </p>
                </div>
                <div>
                  {plan.upi_id && (
                    <div style={{ marginBottom: 16 }}>
                      <p className="dash-table-sub" style={{ margin: '0 0 4px' }}>Or pay to this UPI ID</p>
                      <p style={{ margin: 0, fontSize: 15, fontWeight: 600, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {plan.upi_id}
                      </p>
                      <button
                        type="button"
                        className="setup-link-btn"
                        style={{ marginTop: 4 }}
                        onClick={() => {
                          navigator.clipboard?.writeText(plan.upi_id);
                          setToast('UPI ID copied');
                          setTimeout(() => setToast(null), 2000);
                        }}
                      >
                        Copy UPI ID
                      </button>
                    </div>
                  )}
                  {plan.payment_reference && (
                    <div style={{ marginBottom: 16, padding: 10, border: '1px solid #fed7aa', background: '#fff7ed', borderRadius: 6 }}>
                      <p className="dash-table-sub" style={{ margin: '0 0 2px' }}>Your payment reference</p>
                      <p style={{ margin: 0, fontSize: 17, fontWeight: 700, fontFamily: 'monospace' }}>
                        {plan.payment_reference}
                      </p>
                      <p className="dash-table-sub" style={{ margin: '4px 0 0' }}>
                        Already included if you scan the QR. If you pay by any other method, quote this so
                        we can match your payment and activate you quickly.
                      </p>
                    </div>
                  )}
                  <p style={{ margin: '0 0 10px', fontWeight: 600 }}>After paying, contact us to activate:</p>
                  <p className="dash-table-sub" style={{ margin: '0 0 4px' }}>{SUPPORT.company}</p>
                  <p style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>
                    <a href={`tel:${SUPPORT.phone}`} style={{ color: '#c2410c', textDecoration: 'none' }}>
                      📞 {SUPPORT.phone}
                    </a>
                  </p>
                  <p className="dash-table-sub" style={{ margin: 0 }}>
                    <a href={`mailto:${SUPPORT.email}`} style={{ color: '#c2410c' }}>{SUPPORT.email}</a>
                  </p>
                </div>
              </div>
            </div>
            );
          })()}

          {isOwnerAdmin && (
            <div className="dash-card">
              <h2 className="dash-card-title">Activate plan (Krosifin admin only)</h2>
              <p className="dash-table-sub" style={{ marginBottom: 16 }}>
                Payment is collected offline, so mark a company Pro here once their money has arrived.
                Customers cannot change their own plan. Match the duration to the amount they actually
                paid — these buttons only set access, they do not charge anything.
              </p>
              <div className="dash-btn-row">
                {(plan.billing_options || []).map((o) => (
                  <button
                    key={o.months}
                    className="dash-btn dash-btn-orange"
                    type="button"
                    disabled={planSaving}
                    onClick={() => setCompanyPlan('pro', o.months)}
                  >
                    Activate Pro — {o.months === 1 ? '1 month' : `${o.months} months`} ({inr(o.total_inr)})
                  </button>
                ))}
                <button className="dash-btn cal-btn-grey" type="button" disabled={planSaving} onClick={() => setCompanyPlan('pro', null)}>
                  Pro — no expiry
                </button>
                <button className="dash-btn-red-outline" type="button" disabled={planSaving} onClick={() => setCompanyPlan('free', null)}>
                  Move to Free
                </button>
              </div>
            </div>
          )}

          <div className="dash-card">
            <h2 className="dash-card-title">Help &amp; Contact</h2>
            <p className="dash-table-sub" style={{ marginBottom: 6 }}>{SUPPORT.company}</p>
            <p style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 600 }}>
              <a href={`tel:${SUPPORT.phone}`} style={{ color: '#c2410c', textDecoration: 'none' }}>📞 {SUPPORT.phone}</a>
            </p>
            <p className="dash-table-sub" style={{ margin: 0 }}>
              <a href={`mailto:${SUPPORT.email}`} style={{ color: '#c2410c' }}>{SUPPORT.email}</a>
            </p>
          </div>
        </>
      )}

      {activeTab === 'Users' && canManageUsers && (
        <div className="dash-card">
          <div className="page-title-row">
            <h2 className="dash-card-title">Users</h2>
            <button className="dash-btn dash-btn-orange" type="button" onClick={openAddUser}>
              + Add New User
            </button>
          </div>
          <p className="dash-table-sub" style={{ marginBottom: 16 }}>
            {isOwnerAdmin ? (
              <>
                Owner/Admin has full access to everything. Consultant/Staff is limited to the companies
                or specific clients you assign them below (no assignment = no access). Client is
                read-only and sees only their own client's data. Use "Assignments" to control exactly
                what a Consultant/Staff or Client account can see.
              </>
            ) : (
              <>
                Users you add here get access to <strong>{selectedCompany?.name || 'your company'}</strong> and
                nothing else. Admin can manage users and settings; Consultant/Staff can work in the app but
                not manage users; Client is read-only. Use "Assignments" to narrow someone to specific
                clients instead of the whole company.
              </>
            )}
          </p>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={5}>No users yet.</td>
                </tr>
              )}
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{ROLE_LABELS[u.role] || u.role}</td>
                  <td>
                    <span className={`dash-pill ${u.is_active ? 'dash-pill-green' : 'dash-pill-blue'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEditUser(u)}>
                        Edit
                      </button>
                      {u.role !== 'owner_admin' && (
                        <button className="dash-btn dash-btn-teal" type="button" onClick={() => openAssignments(u)}>
                          Assignments
                        </button>
                      )}
                      {u.id !== currentUser?.id && (
                        <>
                          <button className="dash-btn cal-btn-grey" type="button" onClick={() => toggleUserActive(u)}>
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button className="dash-btn-red-outline" type="button" onClick={() => setUserDeleteTarget(u)}>
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'Alert Preferences' && alertPrefsForm && (
        <div className="dash-card">
          <h2 className="dash-card-title">Alert Preferences — {selectedCompany?.name || ''}</h2>

          {alertPrefs && !alertPrefs.email_configured && (
            <p className="dash-error" style={{ marginBottom: 16 }}>
              Email sending isn't configured on the server yet (SMTP_HOST/SMTP_USERNAME/SMTP_PASSWORD are
              blank in backend/.env), so alerts won't actually send until that's set up — but you can still
              set preferences below now.
            </p>
          )}

          <div className="field-row">
            <div className="field-group" style={{ maxWidth: 420 }}>
              <span className="field-label">Notification email</span>
              <input
                className="field-input"
                type="email"
                placeholder="Defaults to the owner/admin's login email if left blank"
                value={alertPrefsForm.notification_email}
                onChange={(e) => setAlertPrefsForm({ ...alertPrefsForm, notification_email: e.target.value })}
              />
            </div>
            <div className="field-group" style={{ maxWidth: 260 }}>
              <span className="field-label">Notification mobile</span>
              <input
                className="field-input"
                type="tel"
                placeholder="For SMS/WhatsApp once built"
                value={alertPrefsForm.notification_mobile}
                onChange={(e) => setAlertPrefsForm({ ...alertPrefsForm, notification_mobile: e.target.value })}
              />
            </div>
          </div>

          <div className="field-group" style={{ marginBottom: 16 }}>
            <span className="field-label">Additional emails (management, comma-separated)</span>
            <input
              className="field-input"
              placeholder="cfo@company.com, director@company.com"
              value={alertPrefsForm.additional_emails}
              onChange={(e) => setAlertPrefsForm({ ...alertPrefsForm, additional_emails: e.target.value })}
            />
            <p className="dash-table-sub" style={{ margin: '6px 0 0' }}>
              Every address here is CC'd alongside the notification email on every alert sent for this company.
            </p>
          </div>

          <div className="field-group" style={{ marginBottom: 16 }}>
            <span className="field-label">Additional mobiles (comma-separated)</span>
            <input
              className="field-input"
              placeholder="9876543210, 9123456780"
              value={alertPrefsForm.additional_mobiles}
              onChange={(e) => setAlertPrefsForm({ ...alertPrefsForm, additional_mobiles: e.target.value })}
            />
            <p className="dash-table-sub" style={{ margin: '6px 0 0' }}>
              Stored for when SMS/WhatsApp alerts are built — not used for sending yet.
            </p>
          </div>

          <div className="toggle-row">
            <span className="toggle-row-label">
              Email reminders{' '}
              <input
                type="number"
                min="1"
                max="30"
                className="field-input"
                style={{ width: 56, display: 'inline-block', margin: '0 6px' }}
                value={alertPrefsForm.reminder_days_before}
                onChange={(e) => setAlertPrefsForm({ ...alertPrefsForm, reminder_days_before: e.target.value })}
                disabled={!alertPrefsForm.due_date_reminder_enabled}
              />{' '}
              days before due date
            </span>
            <button
              type="button"
              className={`dash-pill ${alertPrefsForm.due_date_reminder_enabled ? 'dash-pill-green' : 'dash-pill-blue'}`}
              style={{ border: 'none', cursor: 'pointer' }}
              onClick={() => setAlertPrefsForm({ ...alertPrefsForm, due_date_reminder_enabled: !alertPrefsForm.due_date_reminder_enabled })}
            >
              {alertPrefsForm.due_date_reminder_enabled ? 'ON' : 'OFF'}
            </button>
          </div>

          <div className="toggle-row">
            <span className="toggle-row-label">WhatsApp alerts</span>
            <span className="dash-pill dash-pill-amber" title="Needs a WhatsApp Business API provider (e.g. Twilio, Gupshup) - not built yet">
              Not available yet
            </span>
          </div>

          <div className="toggle-row">
            <span className="toggle-row-label">SMS alerts</span>
            <span className="dash-pill dash-pill-amber" title="Needs an SMS gateway account (e.g. Twilio, MSG91) - not built yet">
              Not available yet
            </span>
          </div>

          <div className="toggle-row">
            <span className="toggle-row-label">Penalty exposure daily digest</span>
            <button
              type="button"
              className={`dash-pill ${alertPrefsForm.penalty_digest_enabled ? 'dash-pill-green' : 'dash-pill-blue'}`}
              style={{ border: 'none', cursor: 'pointer' }}
              onClick={() => setAlertPrefsForm({ ...alertPrefsForm, penalty_digest_enabled: !alertPrefsForm.penalty_digest_enabled })}
            >
              {alertPrefsForm.penalty_digest_enabled ? 'ON' : 'OFF'}
            </button>
          </div>

          {testEmailStatus && (
            <p className={testEmailStatus.ok ? 'dash-table-sub' : 'dash-error'} style={{ marginTop: 12 }}>
              {testEmailStatus.message}
            </p>
          )}

          <div className="dash-btn-row" style={{ marginTop: 16 }}>
            <button className="dash-btn cal-btn-grey" type="button" disabled={sendingTestEmail} onClick={sendTestAlertEmail}>
              {sendingTestEmail ? 'Sending...' : 'Send Test Email'}
            </button>
            <button className="dash-btn dash-btn-orange" type="button" disabled={savingAlertPrefs} onClick={saveAlertPreferences}>
              {savingAlertPrefs ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        </div>
      )}

      {tdsModalOpen && (
        <div className="ncf-modal-overlay" onClick={() => setTdsModalOpen(false)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">{editingTdsId ? 'Edit TDS Section' : 'Add New TDS Section'}</h3>
            <form onSubmit={handleTdsSubmit}>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">Old Section</span>
                  <input
                    className="field-input"
                    value={tdsForm.old_section}
                    onChange={(e) => setTdsForm({ ...tdsForm, old_section: e.target.value })}
                  />
                </div>
                <div className="field-group">
                  <span className="field-label">New Section *</span>
                  <input
                    className="field-input"
                    required
                    value={tdsForm.new_section}
                    onChange={(e) => setTdsForm({ ...tdsForm, new_section: e.target.value })}
                  />
                </div>
              </div>
              <div className="field-group">
                <span className="field-label">Description</span>
                <input
                  className="field-input"
                  value={tdsForm.description}
                  onChange={(e) => setTdsForm({ ...tdsForm, description: e.target.value })}
                />
              </div>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">Rate % (leave blank for slab rate)</span>
                  <input
                    type="number"
                    step="0.01"
                    className="field-input"
                    value={tdsForm.rate_percent}
                    onChange={(e) => setTdsForm({ ...tdsForm, rate_percent: e.target.value })}
                  />
                </div>
                <div className="field-group">
                  <span className="field-label">Threshold Amount ₹</span>
                  <input
                    type="number"
                    className="field-input"
                    value={tdsForm.threshold_amount}
                    onChange={(e) => setTdsForm({ ...tdsForm, threshold_amount: e.target.value })}
                  />
                </div>
              </div>
              <div className="field-group">
                <span className="field-label">Effective From</span>
                <input
                  type="date"
                  className="field-input"
                  value={tdsForm.effective_from || ''}
                  onChange={(e) => setTdsForm({ ...tdsForm, effective_from: e.target.value })}
                />
              </div>
              <div className="field-group">
                <span className="field-label">Remarks</span>
                <input
                  className="field-input"
                  value={tdsForm.remarks}
                  onChange={(e) => setTdsForm({ ...tdsForm, remarks: e.target.value })}
                />
              </div>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setTdsModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={tdsSaving}>
                  {tdsSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="ncf-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete Company</h3>
            <p className="ncf-modal-body-text" style={{ fontSize: 14, color: '#374151' }}>
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This will permanently
              delete all compliance data for this company. This cannot be undone.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {regenTarget && (
        <div className="ncf-modal-overlay" onClick={() => setRegenTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Rebuild compliance calendar</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Rebuild the calendar for <strong>{regenTarget.name}</strong> from its current company type
              {regenTarget.company_type ? ` (${COMPANY_TYPE_LABELS[regenTarget.company_type] || regenTarget.company_type})` : ''}.
            </p>
            <p className="dash-table-sub">
              Anything with work on it is kept: filed, in-progress, imported or noted items are left
              exactly as they are and are never duplicated. Items with nothing filled in are rebuilt —
              including overdue ones, since an overdue item you never filed is just a pending one whose
              date passed, and leaving a form that never applied would keep accruing penalty against you.
            </p>
            {!regenTarget.company_type && (
              <p className="dash-error">
                This company has no type set, so only the common forms will be generated — no ROC forms.
                Set the company type with Edit first.
              </p>
            )}
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setRegenTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" type="button" onClick={confirmRegenerate}>
                Rebuild Calendar
              </button>
            </div>
          </div>
        </div>
      )}

      {deletePreview && (
        <div className="ncf-modal-overlay" onClick={() => setDeletePreview(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">
              {['gst', 'roc', 'tds_deposit', 'tds_return_filing'].includes(deletePreview.history.module) ? 'Revert' : 'Delete'} Import Batch —{' '}
              {IMPORT_MODULE_LABELS[deletePreview.history.module] || deletePreview.history.module}
            </h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              This batch was imported from <strong>{deletePreview.history.file_name}</strong> on{' '}
              {new Date(deletePreview.history.imported_at).toLocaleString('en-IN')}.
            </p>
            <p style={{ fontSize: 14, color: '#374151' }}>
              <strong>{deletePreview.deletable_count}</strong> record
              {deletePreview.deletable_count === 1 ? '' : 's'} will be{' '}
              {['gst', 'roc', 'tds'].includes(deletePreview.history.module) ? 'reverted to unfiled' : 'deleted'} —
              these are exactly as this import left them, untouched since.
            </p>
            {deletePreview.modified_since_count > 0 && (
              <>
                <p style={{ fontSize: 14, color: '#b45309', fontWeight: 600 }}>
                  {deletePreview.modified_since_count} record{deletePreview.modified_since_count === 1 ? '' : 's'} will
                  be skipped — edited by hand since this import ran, so removing them would lose your correction:
                </p>
                <ul style={{ fontSize: 13, color: '#374151', paddingLeft: 20 }}>
                  {deletePreview.modified_since_details.map((d, idx) => (
                    <li key={idx}>{d}</li>
                  ))}
                </ul>
              </>
            )}
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setDeletePreview(null)}>
                Cancel
              </button>
              <button
                className="dash-btn dash-btn-orange"
                style={{ background: '#dc2626' }}
                type="button"
                disabled={deletingBatch || deletePreview.deletable_count === 0}
                onClick={confirmDeleteBatch}
              >
                {deletingBatch
                  ? 'Working...'
                  : `Yes, ${['gst', 'roc', 'tds'].includes(deletePreview.history.module) ? 'Revert' : 'Delete'} ${deletePreview.deletable_count} Record${deletePreview.deletable_count === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {userModalOpen && (
        <div className="ncf-modal-overlay" onClick={() => setUserModalOpen(false)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">{editingUserId ? 'Edit User' : 'Add New User'}</h3>
            <form onSubmit={handleUserSubmit}>
              <div className="field-group">
                <span className="field-label">Name *</span>
                <input
                  className="field-input"
                  required
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                />
              </div>
              <div className="field-group">
                <span className="field-label">Email *</span>
                <input
                  className="field-input"
                  type="email"
                  required
                  disabled={!!editingUserId}
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                />
              </div>
              <div className="field-group">
                <span className="field-label">{editingUserId ? 'New Password (leave blank to keep current)' : 'Password *'}</span>
                <input
                  className="field-input"
                  type="password"
                  required={!editingUserId}
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                />
              </div>
              <div className="field-group">
                <span className="field-label">Role</span>
                <select
                  className="field-select"
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                >
                  <option value="consultant_staff">Consultant/Staff</option>
                  <option value="client">Client</option>
                  {/* customer_owner = admin of this tenant. owner_admin is
                      platform-wide and sees every company, so it is only ever
                      offered to an existing owner_admin. */}
                  {isCustomerOwner && <option value="customer_owner">Admin</option>}
                  {isOwnerAdmin && <option value="customer_owner">Admin (single company)</option>}
                  {isOwnerAdmin && <option value="owner_admin">Owner/Admin (all companies)</option>}
                </select>
              </div>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setUserModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={userSaving}>
                  {userSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {userDeleteTarget && (
        <div className="ncf-modal-overlay" onClick={() => setUserDeleteTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete User</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Are you sure you want to delete <strong>{userDeleteTarget.name}</strong> ({userDeleteTarget.email})?
              Their company/client assignments will also be removed. This cannot be undone.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setUserDeleteTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" onClick={confirmDeleteUser}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {ledgerMappingModalOpen && (
        <div className="ncf-modal-overlay" onClick={() => setLedgerMappingModalOpen(false)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">{editingLedgerMappingId ? 'Edit Ledger Mapping' : 'Add Ledger Mapping'}</h3>
            <form onSubmit={handleLedgerMappingSubmit}>
              <div className="field-group">
                <span className="field-label">Category</span>
                <select
                  className="field-select"
                  value={ledgerMappingForm.category}
                  onChange={(e) => {
                    const category = e.target.value;
                    setLedgerMappingForm({
                      ...ledgerMappingForm, category, mapped_field: firstFieldForCategory(category), rate: '',
                    });
                  }}
                >
                  {Object.entries(MAPPING_CATEGORY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="field-group">
                <span className="field-label">Krosifin Field</span>
                <select
                  className="field-select"
                  value={ledgerMappingForm.mapped_field}
                  onChange={(e) => setLedgerMappingForm({ ...ledgerMappingForm, mapped_field: e.target.value })}
                >
                  {ledgerMappingForm.category === 'gst' &&
                    Object.entries(GST_FIELD_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  {ledgerMappingForm.category === 'financials' &&
                    Object.entries(FINANCIALS_FIELD_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  {ledgerMappingForm.category === 'tds' &&
                    tdsSections.map((s) => (
                      <option key={s.new_section} value={s.new_section}>
                        {s.new_section}{s.description ? ` - ${s.description}` : ''}
                      </option>
                    ))}
                </select>
                {ledgerMappingForm.category === 'tds' && tdsSections.length === 0 && (
                  <span style={{ fontSize: 12, color: '#b45309' }}>
                    No TDS sections set up yet - add some under the TDS Sections tab first.
                  </span>
                )}
              </div>
              {ledgerMappingForm.category === 'tds' && (
                <div className="field-group">
                  <span className="field-label">Rate % (as deducted on this ledger) *</span>
                  <input
                    className="field-input"
                    type="number"
                    step="0.01"
                    required
                    value={ledgerMappingForm.rate}
                    onChange={(e) => setLedgerMappingForm({ ...ledgerMappingForm, rate: e.target.value })}
                  />
                </div>
              )}
              <div className="field-group">
                <span className="field-label">Tally Ledger Name *</span>
                <input
                  className="field-input"
                  required
                  value={ledgerMappingForm.tally_ledger_name}
                  onChange={(e) => setLedgerMappingForm({ ...ledgerMappingForm, tally_ledger_name: e.target.value })}
                />
              </div>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setLedgerMappingModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={ledgerMappingSaving}>
                  {ledgerMappingSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {ledgerMappingDeleteTarget && (
        <div className="ncf-modal-overlay" onClick={() => setLedgerMappingDeleteTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete Ledger Mapping</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Remove the mapping of <strong>{ledgerMappingDeleteTarget.tally_ledger_name}</strong> to{' '}
              <strong>{ledgerMappingDeleteTarget && mappingFieldLabel(ledgerMappingDeleteTarget)}</strong>?
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setLedgerMappingDeleteTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" onClick={confirmDeleteLedgerMapping}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ImportModal
        open={ledgerMappingImportOpen}
        onClose={() => setLedgerMappingImportOpen(false)}
        module="tally_ledger_mapping"
        moduleLabel="Tally Ledger Mapping"
        companyId={selectedCompanyId}
        onImported={refreshLedgerMappings}
      />

      {apiKeyModalOpen && (
        <div className="ncf-modal-overlay" onClick={() => setApiKeyModalOpen(false)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Generate New API Key</h3>
            <form onSubmit={handleApiKeySubmit}>
              <div className="field-group">
                <span className="field-label">Label *</span>
                <input
                  className="field-input"
                  required
                  placeholder="e.g. Tally Connector - Head Office"
                  value={apiKeyForm.label}
                  onChange={(e) => setApiKeyForm({ label: e.target.value })}
                />
              </div>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setApiKeyModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={apiKeySaving}>
                  {apiKeySaving ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {newApiKeyPlaintext && (
        <div className="ncf-modal-overlay" onClick={() => setNewApiKeyPlaintext(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">API Key Generated</h3>
            <p style={{ fontSize: 14, color: '#dc2626', fontWeight: 600 }}>
              Copy this key now — it will not be shown again.
            </p>
            <div className="field-group">
              <span className="field-label">{newApiKeyPlaintext.label}</span>
              <input
                className="field-input"
                readOnly
                value={newApiKeyPlaintext.plaintext_key}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={copyApiKeyToClipboard}>
                Copy to Clipboard
              </button>
              <button className="dash-btn dash-btn-orange" type="button" onClick={() => setNewApiKeyPlaintext(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {apiKeyRevokeTarget && (
        <div className="ncf-modal-overlay" onClick={() => setApiKeyRevokeTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Revoke API Key</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Revoke <strong>{apiKeyRevokeTarget.label}</strong>? Any Tally Connector using this key will
              immediately be rejected. This cannot be undone — generate a new key if needed afterward.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setApiKeyRevokeTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" onClick={confirmRevokeApiKey}>
                Yes, Revoke
              </button>
            </div>
          </div>
        </div>
      )}

      {assignmentsUser && (
        <div className="ncf-modal-overlay" onClick={closeAssignments}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Assignments — {assignmentsUser.name}</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
              Add a company for whole-company access, or a company + specific client to restrict
              this user to just that client's data.
            </p>

            {userAssignments.length === 0 ? (
              <p style={{ fontSize: 14, color: '#374151' }}>No assignments yet — this user has no access to any company.</p>
            ) : (
              <table className="dash-table" style={{ marginBottom: 16 }}>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Access</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {userAssignments.map((a) => (
                    <tr key={a.id}>
                      <td>{companyName(a.company_id)}</td>
                      <td>{a.client_id ? `Client only: ${assignmentClientNames[a.client_id] || `#${a.client_id}`}` : 'Whole company'}</td>
                      <td>
                        <button className="dash-btn-red-outline" type="button" onClick={() => removeAssignment(a.id)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <form onSubmit={addAssignment}>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">Company</span>
                  <select
                    className="field-select"
                    required
                    value={assignmentForm.company_id}
                    onChange={(e) => handleAssignmentCompanyChange(e.target.value)}
                  >
                    <option value="">Select company</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="field-group">
                  <span className="field-label">Client (optional — restricts to this client only)</span>
                  <select
                    className="field-select"
                    value={assignmentForm.client_id}
                    disabled={!assignmentForm.company_id}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, client_id: e.target.value })}
                  >
                    <option value="">Whole company</option>
                    {assignmentClients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name_of_client}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={closeAssignments}>
                  Close
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={assignmentSaving || !assignmentForm.company_id}>
                  {assignmentSaving ? 'Adding...' : '+ Add Assignment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {gstWarningPending && (
        <div className="ncf-modal-overlay" onClick={() => setGstWarningPending(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Are you sure?</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              GSTR-1 and GSTR-3B are legally mandatory every tax period once GST-registered, even with
              nil turnover — Section 39(8) of the CGST Act requires a return "whether or not any
              supplies... have been made during such tax period." A skipped nil return still carries
              late-fee exposure.
            </p>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Only mark this Not Applicable if GST registration itself wasn't active for this period
              (new registration, cancellation, suspension) — not simply because turnover was zero. If
              you need a monthly-to-quarterly frequency change, that's the QRMP setting on Company
              Profile, not this.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setGstWarningPending(null)}>
                Cancel
              </button>
              <button
                className="dash-btn dash-btn-orange"
                style={{ background: '#dc2626' }}
                type="button"
                onClick={() => {
                  gstWarningPending.proceed();
                  setGstWarningPending(null);
                }}
              >
                Yes, Mark Not Applicable
              </button>
            </div>
          </div>
        </div>
      )}

      {periodModalForm && (
        <div className="ncf-modal-overlay" onClick={closePeriodModal}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Periods — {periodModalForm.form_name}</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
              Each rule applies from that period onward, until a later rule supersedes it — set
              "Applicable from Q2 FY 2026-27" once and it keeps applying every quarter after,
              including future years, with nothing further to do.
            </p>

            {periodRules.length === 0 ? (
              <p style={{ fontSize: 14, color: '#374151' }}>No period-specific rules yet — this form follows the whole-company default above.</p>
            ) : (
              <table className="dash-table" style={{ marginBottom: 16 }}>
                <thead>
                  <tr>
                    <th>From</th>
                    <th>Applicability</th>
                    <th>Remarks</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {periodRules.map((r) => (
                    <tr key={r.id}>
                      <td>{r.fy_year ? `FY ${r.fy_year}-${String((r.fy_year + 1) % 100).padStart(2, '0')} ${periodLabel(r.period)}` : 'All FYs (blanket)'}</td>
                      <td>{r.applicability}</td>
                      <td className="remarks-cell" title={r.remarks || ''}>{r.remarks || '—'}</td>
                      <td>
                        <button className="dash-btn-red-outline" type="button" onClick={() => removePeriodRule(r.id)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <form onSubmit={addPeriodRule}>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">FY Start Year</span>
                  <input
                    type="number"
                    className="field-input"
                    required
                    value={periodRuleForm.fy_year}
                    onChange={(e) => setPeriodRuleForm({ ...periodRuleForm, fy_year: e.target.value })}
                  />
                </div>
                <div className="field-group">
                  <span className="field-label">From</span>
                  <select
                    className="field-select"
                    value={periodRuleForm.period}
                    onChange={(e) => setPeriodRuleForm({ ...periodRuleForm, period: e.target.value })}
                  >
                    {periodOptionsFor(periodModalForm.frequency).map((p) => (
                      <option key={p} value={p}>{periodLabel(p)}</option>
                    ))}
                  </select>
                </div>
                <div className="field-group">
                  <span className="field-label">Applicability</span>
                  <select
                    className="field-select"
                    value={periodRuleForm.applicability}
                    onChange={(e) => setPeriodRuleForm({ ...periodRuleForm, applicability: e.target.value })}
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="NA">NA</option>
                  </select>
                </div>
              </div>
              <div className="field-group">
                <span className="field-label">Remarks</span>
                <input
                  className="field-input"
                  placeholder="e.g. hired first salaried employee"
                  value={periodRuleForm.remarks}
                  onChange={(e) => setPeriodRuleForm({ ...periodRuleForm, remarks: e.target.value })}
                />
              </div>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={closePeriodModal}>
                  Close
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={periodSaving || !periodRuleForm.fy_year}>
                  {periodSaving ? 'Saving...' : '+ Add Rule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );
}

export default Settings;
